import {styled} from "@mui/material/styles"
import {breakpoints} from "@next/themes"
import {Button, Popper} from "@mui/material"

export const TitleWrapper = styled("div")`
    font-size: 1.2rem;
    font-weight: ${({theme}) => theme.colours.font.primary.medium.weight};
`

export const SimplifiedButton = styled(Button)`
    && {
        border-radius: 0;
        border: none;
        box-shadow: none;

        &:hover {
            transition: none;
            border: none;
            box-shadow: none;
        }
        &:focus {
            border: none;
            box-shadow: none;
            border-radius: 0;
        }
        & > span {
            white-space: nowrap;
        }
    }
`

export const LabelButton = styled(SimplifiedButton, {
    shouldForwardProp: prop => !String(prop).startsWith("$"),
})<{$isFilterSelected: boolean}>`
    && {
        transition: none;
        color: ${({theme}) => theme.colours.text.default};
        background-color: ${({theme, $isFilterSelected}) =>
            $isFilterSelected ? theme.colours.utilities.background.default : theme.colours.utilities.background.paper};
        font-size: 0.875rem;
        font-family: ${({theme}) => `${theme.colours.font.primary.regular.family}, ${theme.colours.font.default}`};
        font-weight: ${({theme}) => `${theme.colours.typography.body1.fontWeight}`};
        padding: 0;
        justify-content: flex-start;
        min-width: 0;
    }
`

export const LabelWrapper = styled("div")`
    && {
        display: flex;
        justify-content: space-between;
        align-items: center;
        min-height: 2.75rem;
        padding: 0 1rem;
        background-color: ${({theme}) => theme.colours.popover.header.backgroundColour};
    }
`

export const ViewAllLabel = styled("span")`
    color: ${({theme}) => theme.colours.form.buttonPrimary.background};
    font-weight: 600;
    cursor: pointer;
`

export const Wrapper = styled("div")`
    font-weight: ${({theme}) => theme.colours.font.primary.regular.weight};
    font-family: ${({theme}) => theme.colours.font.primary.regular.family}, ${({theme}) => theme.colours.font.default};
    width: 100%;
    height: 100%;
    display: none;
    @media (min-width: ${breakpoints.values.lg}px) {
        display: block;
    }
`

export const ClearAllButton = styled(SimplifiedButton)`
    && {
        padding: 0;
        background-color: transparent;
        color: ${({theme}) => theme.colours.text.default};
        &:hover {
            background-color: ${({theme}) => theme.colours.form.input.background};
        }
        &:focus {
            background-color: ${({theme}) => theme.colours.form.input.background};
        }
    }
`
export const PopperWrapper = styled(Popper)<{$facetBoxMinWidth: number}>`
    font-weight: ${({theme}) => theme.colours.font.primary.regular.weight};
    font-family: ${({theme}) => theme.colours.font.primary.regular.family}, ${({theme}) => theme.colours.font.default};
    font-size: 0.875rem;
    min-width: ${({$facetBoxMinWidth}) => `${$facetBoxMinWidth}rem`};
    background-color: white;
    z-index: 999;
    box-shadow: 0rem 0.0625rem 0.1875rem 0rem rgb(0 0 0 / 12%), 0rem 0.0625rem 0.0625rem 0rem rgb(0 0 0 / 14%),
        0rem 0.125rem 0.0625rem -0.0625rem rgb(0 0 0 / 20%);
`

export const FilterWrapper = styled("div", {
    shouldForwardProp: prop => !String(prop).startsWith("$"),
})<{$isFilterSelected: boolean; $anyOptionSelected: boolean}>`
    display: flex;
    align-items: center;
    height: 100%;
    cursor: pointer;
    justify-content: flex-start;
    padding: 0 1rem 0.1875rem;
    width: 100%;
    border-top: 3px solid transparent;
    ${({$anyOptionSelected, theme}) =>
        $anyOptionSelected
            ? `
                border-top: 3px solid ${theme.colours.palette.primary.dark};
                `
            : ``};
    ${({$isFilterSelected, theme}) =>
        $isFilterSelected
            ? `
            border-top: 3px solid ${theme.colours.form.buttonPrimary.background};
            background-color: ${theme.colours.utilities.background.default};
            `
            : ``};
    ${({$isFilterSelected, $anyOptionSelected, theme}) =>
        !$isFilterSelected &&
        !$anyOptionSelected &&
        `
                &:hover {
                    border-top: 3px solid ${theme.colours.palette.primary.main};`}
`

export const FilterButton = styled("div", {
    shouldForwardProp: prop => !String(prop).startsWith("$"),
})<{$isFilterSelected: boolean}>`
    && {
        font-size: 0.875rem;
        font-weight: ${({theme, $isFilterSelected}) =>
            `${
                $isFilterSelected ? theme.colours.font.primary.medium.weight : theme.colours.font.primary.regular.weight
            }`};
        font-family: ${({theme, $isFilterSelected}) =>
            `${
                $isFilterSelected ? theme.colours.font.primary.medium.family : theme.colours.font.primary.regular.family
            }, ${theme.colours.font.default}`};
        margin-right: 1rem;
        white-space: nowrap;

        /* this is to prevent width change after bolding the text */
        &:after {
            content: attr(data-text);
            content: attr(data-text) / "";
            display: block;
            height: 0;
            visibility: hidden;
            overflow: hidden;
            user-select: none;
            pointer-events: none;
            font-weight: ${({theme}) => theme.colours.font.primary.medium.weight};
        }
    }
`

export const ButtonText = styled("span", {
    shouldForwardProp: prop => !String(prop).startsWith("$"),
})<{$isOnSecondRow?: boolean}>`
    white-space: nowrap;
    max-width: ${({$isOnSecondRow}) => ($isOnSecondRow ? `12rem` : `6rem`)};
    overflow-x: hidden;
    text-overflow: ellipsis;
    @media (max-width: ${breakpoints.values.xl}px) {
        max-width: ${({$isOnSecondRow}) => ($isOnSecondRow ? `10rem` : `4.3rem`)};
    }
`

export const LabelFilterByText = styled("div")`
    font-size: 0.75rem;
    text-align: left;
    @media (min-width: ${breakpoints.values.lg}px) {
        display: none;
    }
`

export const ViewAllButton = styled(SimplifiedButton)`
    && {
        color: ${({theme}) => theme.colours.palette.primary.main};
        background-color: ${({theme}) => theme.colours.popover.backgroundColour};
        background-color: ${({theme}) => theme.colours.popover.header.backgroundColour};
        font-size: 0.875rem;
        font-family: ${({theme}) => `${theme.colours.font.primary.regular.family}, ${theme.colours.font.default}`};
        font-weight: ${({theme}) => `${theme.colours.font.primary.regular.weight}`};
    }
`

export const FacetLabel = styled("div")`
    display: block;
`

export const SelectLabel = styled("span")`
    font-weight: ${({theme}) => `${theme.colours.font.secondary.medium.weight}`};
`
